import { useEltDataSettings } from 'legacy/lut/components/widgets/public/partner360/ELT/context/EltDataSettingsContext'
import { IChangeComputedFieldsByIndexProps } from 'legacy/lut/components/widgets/public/partner360/ELT/models/IChangeComputedFieldsByIndexProps'
import { IChangeComputedFieldsProps } from 'legacy/lut/components/widgets/public/partner360/ELT/models/IChangeComputedFieldsProps'
import React, { useCallback, useMemo } from 'react'

import {
  getFormedChangedComplexData,
  getFormedChangedComplexDataByKey,
} from '../helpers/getFormedChangedCompexData'

interface IProps<T> {
  updateFormData: (data: T[]) => void
  data: T[]
  newEmptyRow?: T
  canAddNewRowCondFn?: (el: T) => boolean
  changeComputedFieldsFn?: (props: IChangeComputedFieldsProps<T>) => T[]
  changeComputedFieldsByIndexFn?: (props: IChangeComputedFieldsByIndexProps<T>) => T[]
}

export function useTableActions<T>({
  updateFormData,
  data,
  newEmptyRow,
  canAddNewRowCondFn,
  changeComputedFieldsFn,
  changeComputedFieldsByIndexFn,
}: IProps<T>) {
  const { dataSettings } = useEltDataSettings()

  const onChange = useCallback(
    (field: string, index: number) => (value: string | number) => {
      const newData = getFormedChangedComplexData<T>({ field, value, index, data })
      if (changeComputedFieldsByIndexFn) {
        const updatedData = changeComputedFieldsByIndexFn({
          field,
          index,
          data: newData,
        })
        updateFormData(updatedData)
      } else {
        updateFormData(newData)
      }
    },
    [data]
  )

  const onChangeByKey = useCallback(
    (keyField: string) => (field: string, keyValue: string) => (value: string | number) => {
      const newData = getFormedChangedComplexDataByKey<T>({
        field,
        value,
        keyValue,
        keyField,
        data,
      })

      if (changeComputedFieldsFn) {
        const updatedData = changeComputedFieldsFn({
          field,
          keyField,
          keyValue,
          data: newData,
          actualValueTypes: dataSettings.ActualsValueTypes,
        })
        updateFormData(updatedData)
      } else {
        updateFormData(newData)
      }
    },
    [data, dataSettings.ActualsValueTypes]
  )

  const addRow = useCallback(() => {
    if (newEmptyRow) {
      const newData: T[] = [...data, newEmptyRow]
      updateFormData(newData)
    }
  }, [data])

  const deleteLastRow = useCallback(() => {
    const newData = data.slice(0, -1)
    updateFormData(newData)
  }, [data])

  const deleteRow = useCallback(
    (index: number) => {
      const newData = data
      newData.splice(index, 1)
      updateFormData(newData)
    },
    [data]
  )

  const canAddNewRow = useMemo(() => {
    if (canAddNewRowCondFn) {
      const emptyRows = data.filter(canAddNewRowCondFn)
      return emptyRows.length === 0
    }
    return false
  }, [JSON.stringify(data)])

  return { onChange, addRow, deleteRow, deleteLastRow, canAddNewRow, onChangeByKey }
}
